// @i18n-ignore-file

import { useCallback, useEffect, useState } from 'react'
import { captureException } from '../components/Sentry/utils'
import { setCookie } from '../utils'
import { loadState, saveState } from '../utils/localStorage'
import { CONFIG_BUILD_ENV } from '@src/config/env'
import { noop } from '@src/utils/functions/utils'
import {
  enableYotpoProdEnv,
  YOTPO_KEYS,
  YOTPO_TRANSLATION_TEXTS,
  FAQS_HTML_FR,
  FAQS_HTML_EN,
  YOTPO_TABLE_TRANSLATIONS,
} from '@src/constants/common.constants'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { parseLocale } from '@src/utils/i18n.utils'
import { getNonce } from '@src/utils/nonce.utils'
import { generateSHA256HashNode } from '@src/utils/generate-sha256-hash'

import { useTranslation } from 'next-i18next'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'

function normalizeKey(s) {
  return s.replace(/\u00A0/g, ' ').trim()
}

function replaceValuesFromMapDeepRecursive(root, map, resolver, depth = 0, maxDepth = 40, seen = new WeakSet()) {
  if (!root || typeof root !== 'object') return
  if (depth >= maxDepth) return
  if (seen.has(root)) return
  seen.add(root)

  const isDomNode =
    (typeof Node !== 'undefined' && root instanceof Node) ||
    (root && typeof root === 'object' && 'nodeType' in root && typeof root.nodeType === 'number')
  if (isDomNode) return

  if (Array.isArray(root)) {
    for (let i = 0; i < root.length; i++) {
      const v = root[i]
      if (typeof v === 'string') {
        const k = normalizeKey(v)
        if (Object.hasOwn?.(map, k) || Object.prototype.hasOwnProperty.call(map, k)) {
          root[i] = resolver(map[k], v)
          continue
        }
      }
      replaceValuesFromMapDeepRecursive(v, map, resolver, depth + 1, maxDepth, seen)
    }
  } else {
    for (const k0 of Object.keys(root)) {
      const v = root[k0]
      if (typeof v === 'string') {
        const k = normalizeKey(v)
        if (Object.hasOwn?.(map, k) || Object.prototype.hasOwnProperty.call(map, k)) {
          root[k0] = resolver(map[k], v)
          continue
        }
      }
      replaceValuesFromMapDeepRecursive(v, map, resolver, depth + 1, maxDepth, seen)
    }
  }
}

const useSwellSDK = (email, customerId, isReloadNotRequired, handleOnPopupClose = noop) => {
  // eslint-disable-next-line i18next/no-literal-string
  const { t } = useTranslation('common')
  const { swellRequiresToken = false } = useStatsigDynamicLocaleConfig()
  const [initialized, setInitialized] = useState(false)
  const loadReferralCouponCode = loadState('referralCouponCode')
  const saveReferralCouponCode = saveState('referralCouponCode')
  const [couponCode, setCouponCode] = useState(loadReferralCouponCode?.code)
  const [loading, setLoading] = useState(loadReferralCouponCode ? !loadReferralCouponCode.code : true)
  const [setup, setSetup] = useState(false)

  const IS_WELCOME_POPUP_PREVIOUSLY_OPENED = '_quince_welcome_popup'
  const SCRIPT_ID = 'swell-sdk'
  const SWELL_SETUP_EVENT = 'swell:setup event'
  const WELCOME_COOKIE_EXPIRY_MINUTES = 1440

  const { locale } = useLocaleContext()
  const { language } = parseLocale(locale)
  const nonce = getNonce()
  const localeConfig = useStatsigDynamicLocaleConfig()

  // Get Yotpo API key from Statsig config (already has fallback built-in)
  const yotpoApiKey = localeConfig?.yotpoApiKey

  useEffect(() => {
    if (!isReloadNotRequired && customerId && initialized) {
      const swellSDK = document.querySelector('#swell-sdk')

      if (swellSDK) {
        swellSDK.parentNode.removeChild(swellSDK)
      }
      const yotpoGuid = enableYotpoProdEnv.includes(CONFIG_BUILD_ENV)
        ? localeConfig?.yotpo?.guid?.prod
        : localeConfig?.yotpo?.guid?.test

      // eslint-disable-next-line i18next/no-literal-string
      const script = document.createElement('script')
      script.src = yotpoGuid ? `https://cdn-loyalty.yotpo.com/loader/${yotpoGuid}.js` : ''
      script.id = SCRIPT_ID
      if (nonce) {
        script.setAttribute('nonce', nonce)
      }

      document.head.appendChild(script)
      localStorage.setItem('swellSkipCart', true)
      //skip swell cart init (to remove 404 error due to swell)
    }
  }, [isReloadNotRequired, customerId, initialized, localeConfig, nonce])

  useEffect(() => {
    if (!email || !customerId) return
    const swellCustomerIdentification = document.querySelector('#swell-customer-identification')

    if (swellCustomerIdentification) {
      // Remove if already exists
      document.body.removeChild(swellCustomerIdentification)
    }

    // eslint-disable-next-line i18next/no-literal-string
    const div = document.createElement('div')
    div.setAttribute('id', 'swell-customer-identification')
    div.setAttribute('data-authenticated', true)
    div.setAttribute('data-email', email)
    div.setAttribute('data-id', customerId)

    // Use dynamic config - if swellRequiresToken is true, add token
    // Note: This is called within useCallback, config comes from outer scope
    if (swellRequiresToken) {
      const hashKey = `${email}${yotpoApiKey}`
      const hash = generateSHA256HashNode(hashKey)
      div.setAttribute('data-token', hash)
    }
    div.style.display = 'none'

    document.body.appendChild(div)
    setTimeout(() => {
      try {
        window.spapi?.loadStoreAccount(() => {})
      } catch (error) {
        captureException(new Error(`Error in loading the user in yotpo. E-mail: ${email}`))
      }
    }, 0)

    setInitialized(true)
  }, [email, customerId, swellRequiresToken, yotpoApiKey])

  const [customerDetails, setCustomerDetails] = useState(null)

  useEffect(() => {
    // eslint-disable-next-line i18next/no-literal-string
    document.addEventListener('swell:setup', function () {
      console.info(SWELL_SETUP_EVENT)
      setSetup(true)
      setCustomerDetails(window.swellAPI.getCustomerDetails())
    })
  }, [])

  useEffect(() => {
    if (!initialized) return
    if (typeof window !== 'undefined' && window.swellAPI) {
      setCustomerDetails(window.swellAPI.getCustomerDetails())
      // When returning to the page via client navigation, swell:setup may not fire again
      // because the SDK is already loaded. Treat swellAPI presence as setup complete.
      setSetup(true)
    }
  }, [initialized])

  const [referralLink, setReferralLink] = useState(null)

  useEffect(() => {
    if (!customerDetails) return
    if (!customerDetails.email) {
      // eslint-disable-next-line i18next/no-literal-string
      document.addEventListener('swell:setup', function () {
        setSetup(true)
        setCustomerDetails(window.swellAPI.getCustomerDetails())
      })
    }

    setReferralLink(customerDetails.referralLink)
  }, [customerDetails])

  const swellTranslations = () => {
    const swellObj = (window.swellConfig ||= {})
    const maxDepth = 20
    replaceValuesFromMapDeepRecursive(swellObj, YOTPO_TRANSLATION_TEXTS, key => t(key), 0, maxDepth)
    if (!window.swellConfig) window.swellConfig = swellObj

    const loc = window.swellConfig.localization || (window.swellConfig.localization = {})
    Object.assign(
      loc,
      Object.values(YOTPO_KEYS).reduce((acc, key) => {
        if (key === 'faqs_html') {
          acc[key] = language === 'fr' ? FAQS_HTML_FR : FAQS_HTML_EN
        } else acc[key] = t(key)
        return acc
      }, {}),
    )
  }

  const showPopUp = useCallback(
    (popupTab = 'earn') => {
      if (!customerDetails && !initialized) return
      swellTranslations()
      window.swellAPI.showRewardsPopup(popupTab)
    },
    [customerDetails, initialized],
  )

  const setReferralCouponCode = () => {
    // eslint-disable-next-line i18next/no-literal-string
    document.addEventListener('swell:referred:customer:discount', function (event) {
      const eventDetails = event.detail
      const generatedCouponCode = eventDetails.coupon_code
      const savedCouponCode = loadReferralCouponCode ? loadReferralCouponCode.code : null

      // Do not open welcome popup
      setCookie(IS_WELCOME_POPUP_PREVIOUSLY_OPENED, true, WELCOME_COOKIE_EXPIRY_MINUTES)

      // Set coupon code which has been generated latest
      if (generatedCouponCode) {
        saveReferralCouponCode({ code: generatedCouponCode })
        setCouponCode(generatedCouponCode)
        setLoading(false)
      } else {
        setCouponCode(savedCouponCode)
        setLoading(false)
      }
    })
  }

  const updateYotpoWidgetTableTranslations = swellIframeElement => {
    const iframeDoc = swellIframeElement.contentDocument || swellIframeElement.contentWindow.document
    if (!iframeDoc) return

    const targetTable = iframeDoc.querySelector('#swell-my-rewards table.table.table-striped')
    if (!targetTable) return

    // eslint-disable-next-line i18next/no-literal-string
    const allCells = targetTable.querySelectorAll('thead th, tbody td')
    allCells.forEach(cell => {
      const originalText = cell.textContent.trim()
      if (originalText in YOTPO_TABLE_TRANSLATIONS) {
        cell.textContent = t(YOTPO_TABLE_TRANSLATIONS[originalText])
      }
    })
  }

  useEffect(() => {
    if (!setup) return
    const swellPopupIframe = document.getElementById('swell-popup')
    let translationTimeoutId = null

    if (!swellPopupIframe) return

    const observer = new MutationObserver(mutationsList => {
      for (let i = 0; i < mutationsList.length || 0; i += 1) {
        const mutation = mutationsList[i]
        if (
          mutation.type === 'attributes' &&
          mutation.target === swellPopupIframe &&
          mutation.attributeName === 'style'
        ) {
          const currentDisplay = window.getComputedStyle(swellPopupIframe).display
          if (currentDisplay === 'none') {
            // Display property of the target <iframe> element changed to 'none', trigger your handler or perform actions
            handleOnPopupClose()
          } else {
            if (translationTimeoutId) {
              clearTimeout(translationTimeoutId)
            }
            translationTimeoutId = setTimeout(() => {
              updateYotpoWidgetTableTranslations(swellPopupIframe)
            }, 1000)
          }
        }
      }
    })

    observer.observe(swellPopupIframe, {
      attributes: true,
      // eslint-disable-next-line i18next/no-literal-string
      attributeFilter: ['style'],
    })
    return () => {
      if (translationTimeoutId) {
        clearTimeout(translationTimeoutId)
      }
      observer.disconnect()
    }
  }, [handleOnPopupClose, setup])

  return {
    referralLink,
    showPopUp,
    setReferralCouponCode,
    couponCode,
    loading,
    setup,
    initialized,
  }
}

export default useSwellSDK
